// services/firestore/audit.ts — Reads for the
// license_org_audit_events collection. Cursor pagination via
// startAfterDocument; the DocumentSnapshot returned by each page
// becomes the cursor for the next.

import {
  collection,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import type { AuditEvent } from '@/types/domain';

export const AUDIT_PAGE_SIZE = 50;
export const AUDIT_RECENT_LIMIT = 5;

function fromDoc(d: QueryDocumentSnapshot): AuditEvent {
  const data = d.data() as Record<string, unknown>;
  return {
    id: d.id,
    licenseOrgId: (data.licenseOrgId as string | undefined) ?? '',
    action: (data.action as AuditEvent['action']) ?? 'unknown',
    actorUid: (data.actorUid as string | undefined) ?? '',
    actorRole: (data.actorRole as AuditEvent['actorRole']) ?? 'unknown',
    targetKind:
      (data.targetKind as AuditEvent['targetKind']) ?? 'unknown',
    targetId: (data.targetId as string | null) ?? null,
    outcome: (data.outcome as AuditEvent['outcome']) ?? 'unknown',
    reasonCode: (data.reasonCode as string | null) ?? null,
    tsServer: (data.tsServer as AuditEvent['tsServer']) ?? null,
    metadata:
      (data.metadata as Record<string, unknown> | undefined) ?? {},
  };
}

export interface AuditPage {
  events: AuditEvent[];
  cursor: DocumentSnapshot | null;
  hasMore: boolean;
}

export async function fetchAuditPage(
  orgId: string,
  startAfterDoc: DocumentSnapshot | null,
  pageSize: number = AUDIT_PAGE_SIZE,
): Promise<AuditPage> {
  const base = query(
    collection(db, 'license_org_audit_events'),
    where('licenseOrgId', '==', orgId),
    orderBy('tsServer', 'desc'),
    fsLimit(pageSize),
  );
  const q = startAfterDoc ? query(base, startAfter(startAfterDoc)) : base;
  const snap = await getDocs(q);
  return {
    events: snap.docs.map(fromDoc),
    cursor: snap.docs[snap.docs.length - 1] ?? null,
    hasMore: snap.docs.length === pageSize,
  };
}

export async function fetchRecentAudit(
  orgId: string,
): Promise<AuditEvent[]> {
  const page = await fetchAuditPage(orgId, null, AUDIT_RECENT_LIMIT);
  return page.events;
}
